import { Link } from "react-router";
import { InfoCircleIcon, cx } from "@wato/ui";
import type { ConsoleMessageView } from "../../../api/console-types";
import { dayLabel, str, type Tr } from "./console-text";

const BUDGET_PILL: Record<string, string> = {
  ok: "bg-success-bg text-success-fg",
  warning: "bg-warning-bg text-warning-fg",
  exceeded: "bg-danger-bg text-danger-fg",
};

/** MC-10: the morning summary card. */
export function SummaryCard({
  m,
  tr,
  locale,
  time,
  answerNowTo,
  billingTo,
}: {
  locale: string;
  m: ConsoleMessageView;
  tr: Tr;
  time: string;
  answerNowTo: string;
  billingTo: string;
}) {
  const p = m.payload;
  const num = (k: string) => (typeof p[k] === "number" ? (p[k] as number) : 0);
  const top = Array.isArray(p.topUnanswered) ? (p.topUnanswered as string[]) : [];
  const pct = typeof p.aiAnsweredPercent === "number" ? `${p.aiAnsweredPercent}%` : "-";
  const budget = str(p.aiBudgetStatus) || "ok";
  const unanswered = num("unanswered");
  const tile = (k: string, v: string | number, tint = "bg-surface-muted text-fg") => (
    <div className={cx("rounded-md px-3 py-2", tint)}>
      <span className="block text-xs text-fg-muted">{tr(`summary.${k}`)}</span>
      <span className="text-lg font-semibold">{v}</span>
    </div>
  );
  return (
    <div className="flex flex-col gap-3" data-testid="console-summary">
      <div className="flex items-center gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-info-bg text-info-fg">
          <InfoCircleIcon aria-hidden="true" className="size-5" />
        </span>
        <div className="min-w-0">
          <p className="font-semibold">
            {tr("summary.title", { date: dayLabel(num("summaryDay"), locale) })}
          </p>
          <p className="text-xs text-fg-muted">{tr("summary.morning", { time })}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {tile("newConversations", num("newConversations"))}
        {tile(
          "unanswered",
          unanswered,
          unanswered > 0 ? "bg-warning-bg text-warning-fg" : undefined,
        )}
        {tile("handoffs", num("handoffs"))}
        {tile("aiAnswered", pct)}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-muted">
        <span>
          {tr("summary.bookings")}: {num("bookings")}
        </span>
        <span>
          {tr("summary.pendingApprovals")}: {num("pendingApprovals")}
        </span>
        <span
          data-testid="console-budget"
          data-status={budget}
          className={cx(
            "rounded-full px-2 py-0.5 font-medium",
            BUDGET_PILL[budget] ?? BUDGET_PILL.ok,
          )}
        >
          {tr("summary.budget.label")}: {tr(`summary.budget.${budget}`)}
        </span>
        {budget !== "ok" && (
          <Link className="font-medium text-fg underline" to={billingTo}>
            {tr("summary.topUp")}
          </Link>
        )}
      </div>
      {top.length > 0 && (
        <div>
          <p className="text-xs font-medium text-fg-muted">{tr("summary.topUnanswered")}</p>
          <ol className="list-decimal ps-5">
            {top.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ol>
          <Link className="text-sm font-medium underline" to={answerNowTo}>
            {tr("summary.answerNow")}
          </Link>
        </div>
      )}
    </div>
  );
}
